import { useState, useEffect } from 'react';
import { Star, Eye, X, MessageSquare, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '../components/Toast';
import api from '../Server';

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  const ist  = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata', hour12: true });
  const us   = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York', hour12: true });
  return { date, ist, us };
}

function StarRating({ rating }) {
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <Star key={star} size={15}
          fill={star <= rating ? '#F59E0B' : 'none'}
          color={star <= rating ? '#F59E0B' : '#D1D5DB'}
        />
      ))}
    </div>
  );
}

function ReviewModal({ item, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Review from {item.full_name}</h3>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Client</label>
            <div style={{ fontWeight: 600 }}>
              {item.full_name}
              <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: 8 }}>
                — {item.company}
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Rating</label>
            <StarRating rating={item.star_rating} />
          </div>
          <div className="form-group">
            <label className="form-label">Review</label>
            <div style={{
              fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.8,
              background: 'var(--border-light)', padding: '14px 16px', borderRadius: 8,
            }}>
              {item.your_review}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            {item.status === 'approved'
              ? <span className="badge badge-green"><CheckCircle size={11} /> &nbsp;Accepted — Live on website</span>
              : <span className="badge badge-yellow">Pending Review</span>
            }
          </div>
          <div className="form-group">
            <label className="form-label">Submitted On</label>
            {(() => {
              const f = formatDate(item.createdAt || item.created_at);
              return f ? (
                <>
                  <div style={{ fontWeight: 600 }}>{f.date}</div>
                  <div style={{ fontSize: 11, color: '#3B82F6', marginTop: 3 }}>🇮🇳 IST &nbsp;{f.ist}</div>
                  <div style={{ fontSize: 11, color: '#8B5CF6', marginTop: 2 }}>🇺🇸 EST &nbsp;{f.us}</div>
                </>
              ) : <div>—</div>;
            })()}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const toast = useToast();
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [viewItem, setViewItem] = useState(null);

  // ── GET /review/getReviews ────────────────────────────────────────────────
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await api.get('/review/getReviews');
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)    ? raw.data
        : Array.isArray(raw?.reviews) ? raw.reviews
        : Array.isArray(raw?.result)  ? raw.result
        : [];
      console.log('[Testimonials] API response:', raw);
      setReviews([...list].reverse());
    } catch (err) {
      console.error('[Testimonials] fetch error:', err);
      toast.error('Load Failed', 'Failed to load reviews. Please try again.');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  // ── ACCEPT — POST /review/updateReview with status: 'approved' ────────────
  const handleAccept = async (item) => {
    try {
      await api.post('/review/updateReview', {
        id:          item.id,
        full_name:   item.full_name,
        company:     item.company,
        star_rating: item.star_rating,
        your_review: item.your_review,
        status:      'approved',
      });
      // update local state immediately
      setReviews(prev =>
        prev.map(r => r.id === item.id ? { ...r, status: 'approved' } : r)
      );
      toast.success('Accepted!', `Review from "${item.full_name}" is now live on the website.`);
    } catch (err) {
      toast.error('Failed', 'Failed to accept review. Please try again.');
    }
  };

  // ── REJECT — POST /review/deleteReview ────────────────────────────────────
  const handleReject = async (item) => {
    const ok = await toast.confirm(`This will permanently remove the review from "${item.full_name}".`, { confirmLabel: 'Reject & Delete', danger: true });
    if (!ok) return;
    try {
      await api.post('/review/deleteReview', { id: item.id });
      setReviews(prev => prev.filter(r => r.id !== item.id));
      toast.error('Rejected', `Review from "${item.full_name}" has been removed.`);
    } catch (err) {
      toast.error('Failed', 'Failed to reject review. Please try again.');
    }
  };

  const safeList  = Array.isArray(reviews) ? reviews : [];
  const pending   = safeList.filter(r => r.status !== 'approved').length;
  const accepted  = safeList.filter(r => r.status === 'approved').length;

  return (
    <div className="page-content">
      <div className="page-header">
        <h2>Testimonials</h2>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: 600 }}>
        <div className="stat-card" style={{ '--card-accent': '#3B82F6', '--card-icon-bg': 'rgba(59,130,246,0.12)' }}>
          <div className="stat-card-header">
            <span className="stat-label">Total</span>
            <div className="stat-icon"><MessageSquare size={18} /></div>
          </div>
          <div className="stat-value">{safeList.length}</div>
        </div>
        <div className="stat-card" style={{ '--card-accent': '#F59E0B', '--card-icon-bg': 'rgba(245,158,11,0.12)' }}>
          <div className="stat-card-header">
            <span className="stat-label">Pending</span>
            <div className="stat-icon"><Star size={18} /></div>
          </div>
          <div className="stat-value">{pending}</div>
        </div>
        <div className="stat-card" style={{ '--card-accent': '#22C55E', '--card-icon-bg': 'rgba(34,197,94,0.12)' }}>
          <div className="stat-card-header">
            <span className="stat-label">Accepted</span>
            <div className="stat-icon"><CheckCircle size={18} /></div>
          </div>
          <div className="stat-value">{accepted}</div>
        </div>
      </div>

      {/* Table */}
      <div className="table-card">
        <div className="table-card-header">
          <h3>All Testimonials ({safeList.length})</h3>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Company</th>
                <th>Rating</th>
                <th>Review</th>
                <th>Status</th>
                <th>Action</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8}><div className="empty-state"><p>Loading reviews...</p></div></td></tr>
              ) : safeList.length === 0 ? (
                <tr><td colSpan={8}><div className="empty-state"><Star size={40} /><p>No reviews yet.</p></div></td></tr>
              ) : safeList.map((item, i) => (
                <tr key={item.id}>
                  <td><strong>{i + 1}</strong></td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{item.full_name}</td>
                  <td style={{ fontSize: 12.5 }}>{item.company}</td>
                  <td><StarRating rating={item.star_rating} /></td>
                  <td style={{ fontSize: 12.5, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.your_review}
                  </td>

                  {/* Status badge */}
                  <td>
                    {item.status === 'approved'
                      ? <span className="badge badge-green"><CheckCircle size={11} />&nbsp;Accepted</span>
                      : <span className="badge badge-yellow">Pending</span>
                    }
                  </td>

                  {/* Accept / Reject / View */}
                  <td>
                    <div className="action-btns">
                      {/* View */}
                      <button className="action-btn view" onClick={() => setViewItem(item)} title="View">
                        <Eye size={15} />
                      </button>

                      {/* Accept — only when pending */}
                      {item.status !== 'approved' && (
                        <button
                          className="action-btn"
                          onClick={() => handleAccept(item)}
                          title="Accept — publish to website"
                          style={{ background: '#F0FDF4', borderColor: '#22C55E', color: '#16A34A' }}
                        >
                          <CheckCircle size={15} />
                        </button>
                      )}

                      {/* Reject */}
                      <button
                        className="action-btn delete"
                        onClick={() => handleReject(item)}
                        title="Reject & remove"
                      >
                        <XCircle size={15} />
                      </button>
                    </div>
                  </td>

                  {/* Created Date */}
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {(() => {
                      const f = formatDate(item.createdAt || item.created_at);
                      return f ? (
                        <>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>{f.date}</div>
                          <div style={{ fontSize: 11, color: '#3B82F6' }}>🇮🇳 IST &nbsp;{f.ist}</div>
                          <div style={{ fontSize: 11, color: '#8B5CF6', marginTop: 2 }}>🇺🇸 EST &nbsp;{f.us}</div>
                        </>
                      ) : '—';
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewItem && <ReviewModal item={viewItem} onClose={() => setViewItem(null)} />}
    </div>
  );
}
